import { useEffect, useState } from "react";
import type { SimEvent } from "@reagent/sim";
import { payoutFor } from "@reagent/meta";
import { useGameStore } from "../app/gameStore";
import { useRunStore } from "../app/runStore";
import { useSettingsStore } from "../app/settingsStore";
import { subscribePerf } from "../render/PerfProbe";
import { audioStatus } from "../audio/AudioEngine";
import "./tuning-console.css";

/**
 * The balance-pass tool.
 *
 * Tuning a stage means running it many times and watching the numbers that
 * decide it — how hot the hottest machine is, how much wall the weakest pipe
 * has left, how the catalyst bed is holding up, what the score is doing. Those
 * are all in the snapshot; what was missing was somewhere to read them without
 * a debugger.
 *
 * It also keeps the M1 exit-bar tool: every SimEvent can still be fired by
 * hand, because an effect that can't be triggered in isolation can't be tuned.
 * Those events are fabricated — they reach the FX layer and never touch the
 * engine, which is why the board doesn't change when you fire one.
 */
const DEBUG_EVENTS: ReadonlyArray<{ label: string; event: SimEvent }> = [
  { label: "packet merged", event: { type: "packet.merged", intoPacketId: "dbg", consumedPacketIds: ["x"], at: { x: 3, y: 2 } } },
  { label: "separated", event: { type: "packet.separated", nodeId: "sep", multiple: 1, mainComposition: { HCl: 1 }, wasteComposition: { Sludge: 1 } } },
  { label: "reaction", event: { type: "reaction.fired", nodeId: "mix", recipeId: "neutralisation", multiple: 1, yieldComposition: { NaCl: 1 }, isExothermic: true } },
  { label: "heated", event: { type: "node.heated", nodeId: "mix", heatCounter: 4, criticalTemp: 4 } },
  { label: "regulated", event: { type: "node.regulated", nodeId: "mix", deltaTemp: 5, packetCount: 2 } },
  { label: "overheat warn", event: { type: "node.overheatWarning", nodeId: "mix", ticksUntilExplosion: 1 } },
  { label: "explode", event: { type: "node.exploded", nodeId: "mix", clearedEdgeIds: [] } },
  { label: "pressured", event: { type: "pipe.pressured", edgeId: "e-c", pressureCounter: 2 } },
  { label: "burst", event: { type: "pipe.burst", edgeId: "e-c", spillAt: { x: 4, y: 2 } } },
  { label: "corroded", event: { type: "pipe.corroded", edgeId: "e-a", hp: 40 } },
  { label: "leaking", event: { type: "pipe.leaking", edgeId: "e-d" } },
  { label: "poisoned", event: { type: "catalyst.poisoned", nodeId: "cat", catalystHealth: 50 } },
  { label: "sludged", event: { type: "packet.sludged", packetId: "dbg", nodeId: "out" } },
  { label: "card slam", event: { type: "card.played", cardId: "dbg", at: { x: 4, y: 3 } } },
  { label: "hardware installed", event: { type: "hardware.installed", nodeId: "cat", edgeId: null, cardId: "card-storage-tank", instanceId: "dbg-install", costCredits: 10, labourKw: 0 } },
  { label: "hardware dismantled", event: { type: "hardware.dismantled", nodeId: "cat", edgeId: null, cardId: "card-storage-tank", instanceId: "dbg-install", refundCredits: 10, labourKw: 0, lostUnits: 0 } },
  { label: "feed exhausted", event: { type: "feed.exhausted", nodeId: "in-a", emittedUnits: 4 } },
  { label: "objective", event: { type: "objective.progressed", collected: { NaCl: 3 }, targetComposition: { NaCl: 6 } } },
  { label: "polluted", event: { type: "deck.polluted", cardId: "card-clogged-line", reason: "sludge", sourceId: "out" } },
  { label: "tile contaminated", event: { type: "tile.contaminated", pos: { x: 4, y: 2 }, cause: "burst", sourceId: "e-c" } },
  { label: "vermin risk", event: { type: "facility.verminRisk", verminRisk: 2, threshold: 3, sourceId: "out" } },
  { label: "node infested", event: { type: "node.infested", nodeId: "split", lostTickPeriod: 2 } },
  { label: "infestation cleared", event: { type: "node.infestationCleared", nodeId: "split", cause: "starved" } },
  { label: "valve set", event: { type: "node.valveSet", nodeId: "split", state: { kind: "closed" }, costKw: 1 } },
  { label: "power outage", event: { type: "facility.powerOutage", cause: "scripted", frozenNodeIds: ["mix"], availableKw: 0 } },
  { label: "structural disaster", event: { type: "facility.disaster", kind: "seismic", affectedIds: ["e-a"] } },
  { label: "run failed", event: { type: "run.failed", reason: "explosion" } },
  { label: "run cleared", event: { type: "run.cleared" } },
];

export function TuningConsole() {
  const snapshot = useGameStore((s) => s.snapshot);
  const stageId = useGameStore((s) => s.stageId);
  const selectStage = useGameStore((s) => s.selectStage);
  const reset = useGameStore((s) => s.reset);
  const fireDebugEvent = useGameStore((s) => s.fireDebugEvent);
  const mode = useGameStore((s) => s.mode);
  const loadRunStage = useGameStore((s) => s.loadRunStage);
  const run = useRunStore((s) => s.run);
  const newRun = useRunStore((s) => s.newRun);

  const [seed, setSeed] = useState(1);
  const [showFx, setShowFx] = useState(false);

  const live = snapshot.nodes.filter((n) => !n.isDestroyed);
  const hottest = [...live].sort((a, b) => b.heatCounter - a.heatCounter)[0];
  const bed = live.find((n) => n.kind === "catalyticBed");
  const weakest = [...snapshot.edges].filter((e) => !e.isBurst).sort((a, b) => a.hp - b.hp)[0];
  const mostPressured = [...snapshot.edges]
    .filter((e) => !e.isBurst)
    .sort((a, b) => b.pressureCounter - a.pressureCounter)[0];

  /**
   * The seed box only holds a number. It used to apply itself on every
   * keystroke, which meant typing "12" first loaded seed 1 — and, because
   * choosing a deal is a free-play action, silently dropped you out of your
   * run mid-shift. Applying it is now always an explicit button.
   */
  const reroll = (next: number) => {
    setSeed(next);
    selectStage(stageId, next);
  };

  return (
    <div className="tuning">
      <div className="tuning__title">
        Tuning
        <span className="tuning__phase">
          {snapshot.phase} · t{snapshot.tickInTurn}/{snapshot.ticksPerTurn} · turn {snapshot.turn}/
          {snapshot.maxTurns}
        </span>
      </div>

      <dl className="tuning__readout">
        <Readout label="hottest" value={hottest ? `${hottest.heatCounter}/${hottest.criticalTemp}` : "—"} />
        <Readout label="bed" value={bed ? `${bed.catalystHealth}%` : "—"} />
        <Readout label="weakest pipe" value={weakest ? `${weakest.hp}/${weakest.maxHp}` : "—"} />
        <Readout label="pressure" value={mostPressured ? String(mostPressured.pressureCounter) : "—"} />
        <Readout label="in flight" value={String(snapshot.packetsInFlight.length)} />
        <Readout label="deck" value={`${snapshot.drawPileCount}+${snapshot.discardPileCount}`} />
        <Readout label="yield" value={`${snapshot.tallies.scoredUnits}/${snapshot.tallies.targetUnits}`} />
        <Readout label="₵" value={String(payoutFor(snapshot.tallies, snapshot.isRunComplete).total)} />
        <FrameReadout />
        <Readout label="audio" value={audioStatus()} />
      </dl>

      <div className="tuning__row">
        <label className="tuning__seed">
          seed
          <input
            type="number"
            min={1}
            value={seed}
            onChange={(e) => setSeed(Math.max(1, Number(e.target.value) || 1))}
          />
        </label>
        {/* Re-dealing is a free-play tool. In a run it would silently drop you
            out of the shift — and being able to re-roll a bad hand mid-run is
            save-scumming, which is not a thing a roguelike should hand you by
            accident. */}
        <button
          disabled={mode === "run"}
          title={mode === "run" ? "re-dealing is free play only" : "deal the next seed"}
          onClick={() => reroll(seed)}
        >
          deal seed
        </button>
        <button onClick={reset}>restart</button>
      </div>

      {/* Run controls. Selecting a stage by hand drops to free play, so there
          has to be a way back — and a run seeded by hand is how a reported run
          gets reproduced. */}
      <div className="tuning__row">
        <span className="tuning__mode">{mode === "run" ? `run ${run.seed}` : "free play"}</span>
        {mode === "free" ? (
          <button onClick={loadRunStage}>back to run</button>
        ) : (
          <button
            onClick={() => {
              newRun(seed);
              loadRunStage();
            }}
          >
            new run @ seed
          </button>
        )}
      </div>

      <button className="tuning__toggle" onClick={() => setShowFx((v) => !v)}>
        {showFx ? "hide" : "show"} effect triggers
      </button>

      {showFx && (
        <div className="tuning__grid">
          {DEBUG_EVENTS.map(({ label, event }) => (
            <button key={label} onClick={() => fireDebugEvent(event)}>
              {label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Frame time, when it has been asked for.
 *
 * It subscribes to the probe rather than reading a store, because the whole
 * point is to measure the render loop without adding a React render to every
 * frame of it — a meter that re-renders the app 60 times a second measures
 * mostly itself.
 */
function FrameReadout() {
  const showPerf = useSettingsStore((s) => s.showPerf);
  const [perf, setPerf] = useState({ ms: 0, fps: 0 });

  useEffect(() => {
    if (!showPerf) return;
    return subscribePerf(setPerf);
  }, [showPerf]);

  if (!showPerf) return null;
  return <Readout label="frame" value={perf.fps > 0 ? `${perf.ms}ms · ${perf.fps}fps` : "…"} />;
}

function Readout({ label, value }: { label: string; value: string }) {
  return (
    <div className="tuning__readout-item">
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}
